import { useReducer } from "react";
import moment from "moment-timezone";
import { AlertTriangle, ChevronDown, ChevronRight, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Activity,
  AUTOMATION_SOURCE_TYPE,
  MAX_FLAG_REASON_CHARACTERS,
  useApproveTodo,
  useChangeTodoCustomer,
  useListCustomers,
  useMarkTodoFlag,
  useRemoveTodoFlag,
  useUpdateTodoFlag,
} from "@/lib/api/customer";
import { CommonDestinationTimezone } from "@/lib/api/document";
import { HideTodoDialog } from "./HideTodoDialog";
import { CharacterCounter } from "./CharacterCounter";
import { TodoContextLine } from "./TodoContextLine";
import { TodoTranscriptContent, useTodoTranscript } from "./TodoTranscript";

interface ManageTodoDialogProps {
  todo: Activity | null;
  onClose: () => void;
  showReviewActions?: boolean;
}

type FlagState = { reason: string; date: string } | null;

type FlagAction = "save" | "update" | "remove" | "approve" | "customer";

type CustomerState = { id: string; name: string };

interface DialogState {
  // The parent hands over a snapshot of the row, which goes stale the moment
  // this dialog changes the flag or customer. Tracking them here keeps the
  // Save/Remove toggle honest without waiting on the list to refetch.
  flag: FlagState;
  reason: string;
  customer: CustomerState;
  selectedCustomerId: string;
  pendingAction: FlagAction | null;
  isTranscriptOpen: boolean;
  isFlagOpen: boolean;
  isDiscardDialogOpen: boolean;
  isHideDialogOpen: boolean;
}

type DialogAction =
  | { type: "reasonChanged"; reason: string }
  | { type: "customerSelected"; customerId: string }
  | { type: "actionStarted"; action: FlagAction }
  | { type: "actionFinished" }
  | { type: "flagSaved"; flag: NonNullable<FlagState> }
  | { type: "flagRemoved" }
  | { type: "customerSaved"; customer: CustomerState }
  | { type: "transcriptToggled"; isOpen: boolean }
  | { type: "flagSectionToggled"; isOpen: boolean }
  | { type: "discardPromptToggled"; isOpen: boolean }
  | { type: "hideDialogToggled"; isOpen: boolean }
  | { type: "changesDiscarded" };

function flagStateOf(todo: Activity | null): FlagState {
  if (!todo?.flagged_date) return null;
  return { reason: todo.flagged_reason ?? "", date: todo.flagged_date };
}

function customerStateOf(todo: Activity | null): CustomerState {
  return { id: todo?.customer_id ?? "", name: todo?.Customer?.long_name ?? "" };
}

function initialStateOf(todo: Activity | null): DialogState {
  const flag = flagStateOf(todo);
  const customer = customerStateOf(todo);
  return {
    flag,
    reason: flag?.reason ?? "",
    customer,
    selectedCustomerId: customer.id,
    pendingAction: null,
    isTranscriptOpen: false,
    isFlagOpen: true,
    isDiscardDialogOpen: false,
    isHideDialogOpen: false,
  };
}

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "reasonChanged":
      return { ...state, reason: action.reason };
    case "customerSelected":
      return { ...state, selectedCustomerId: action.customerId };
    case "actionStarted":
      return { ...state, pendingAction: action.action };
    case "actionFinished":
      return { ...state, pendingAction: null };
    case "flagSaved":
      return { ...state, flag: action.flag, reason: action.flag.reason };
    case "flagRemoved":
      return { ...state, flag: null, reason: "" };
    case "customerSaved":
      return {
        ...state,
        customer: action.customer,
        selectedCustomerId: action.customer.id,
      };
    case "transcriptToggled":
      return { ...state, isTranscriptOpen: action.isOpen };
    case "flagSectionToggled":
      return { ...state, isFlagOpen: action.isOpen };
    case "discardPromptToggled":
      return { ...state, isDiscardDialogOpen: action.isOpen };
    case "hideDialogToggled":
      return { ...state, isHideDialogOpen: action.isOpen };
    case "changesDiscarded":
      return {
        ...state,
        reason: state.flag?.reason ?? "",
        selectedCustomerId: state.customer.id,
        isDiscardDialogOpen: false,
      };
  }
}

// Keying on the todo id remounts the dialog (and so resets its reducer state)
// when a different todo is opened. Refetches hand over a new object for the
// same row and keep the key, so they don't drop local state.
export function ManageTodoDialog({
  todo,
  onClose,
  showReviewActions,
}: ManageTodoDialogProps) {
  return (
    <ManageTodoDialogContent
      key={todo?.id ?? "closed"}
      todo={todo}
      onClose={onClose}
      showReviewActions={showReviewActions}
    />
  );
}

function ManageTodoDialogContent({
  todo,
  onClose,
  showReviewActions,
}: ManageTodoDialogProps) {
  const { toast } = useToast();
  const approveTodo = useApproveTodo();
  const markTodoFlag = useMarkTodoFlag();
  const removeTodoFlag = useRemoveTodoFlag();
  const updateTodoFlag = useUpdateTodoFlag();
  const changeTodoCustomer = useChangeTodoCustomer();
  // Same list as the Transcriptions customer filter; the API also requires a risk profile.
  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const isOpen = todo !== null;

  const [state, dispatch] = useReducer(dialogReducer, todo, initialStateOf);
  const {
    flag,
    reason,
    customer,
    selectedCustomerId,
    pendingAction,
    isTranscriptOpen,
    isFlagOpen,
    isDiscardDialogOpen,
    isHideDialogOpen,
  } = state;

  // Fetched as soon as the dialog opens — not when the section expands — so
  // the transcript is usually ready by the time the user asks for it.
  const transcriptState = useTodoTranscript(todo?.id ?? "", isOpen);

  // Approved or hidden automation todos are read-only here; the transcript stays viewable.
  const isLocked =
    todo?.source_type === AUTOMATION_SOURCE_TYPE && !!(todo.approved_date || todo.hidden_date);
  const canHide = !todo?.hidden_date;
  const canApprove = !todo?.approved_date && !todo?.hidden_date;
  const isFlagged = flag !== null;
  const isSubmitting = pendingAction !== null;
  const savedReason = flag?.reason ?? "";
  const hasUnsavedReason = reason !== savedReason;
  const canSaveChanges =
    isFlagged && reason.trim().length > 0 && reason.trim() !== savedReason;
  const canChangeCustomer = !!showReviewActions && !isLocked;
  const hasUnsavedCustomer = canChangeCustomer && selectedCustomerId !== customer.id;
  const hasUnsavedChanges = hasUnsavedReason || hasUnsavedCustomer;
  const currentTodo: Activity | null = todo && {
    ...todo,
    customer_id: customer.id,
    Customer: { ...todo.Customer, long_name: customer.name },
  };

  const customers: { id: string; long_name: string }[] = customersResult.data?.data || [];
  const customerOptions = customers
    .map((option: { id: string; long_name: string }) => ({ value: option.id, label: option.long_name }))
    .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));

  const closeWithoutConfirmation = () => {
    dispatch({ type: "changesDiscarded" });
    onClose();
  };

  const requestClose = () => {
    if (isSubmitting) return;

    if (hasUnsavedChanges) {
      dispatch({ type: "discardPromptToggled", isOpen: true });
      return;
    }

    closeWithoutConfirmation();
  };

  // The API sanitizes the reason, so the saved text can differ from what was
  // typed; the box shows what was actually stored.
  const applySavedFlag = (entity: Activity) => {
    const saved = {
      reason: entity.flagged_reason ?? reason.trim(),
      date: entity.flagged_date ?? new Date().toISOString(),
    };
    dispatch({ type: "flagSaved", flag: saved });
  };

  const handleApprove = async () => {
    if (!todo || isSubmitting || hasUnsavedChanges) return;

    dispatch({ type: "actionStarted", action: "approve" });

    try {
      const result = await approveTodo({ todo_id: todo.id });

      if (!result.success) {
        toast({
          title: "Unable to approve Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Todo approved",
        description: "The Todo now appears in the To-Dos list.",
      });
      closeWithoutConfirmation();
    } catch {
      toast({
        title: "Unable to approve Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  const handleChangeCustomer = async () => {
    if (!todo || !hasUnsavedCustomer || !selectedCustomerId || isSubmitting) return;

    dispatch({ type: "actionStarted", action: "customer" });

    try {
      const result = await changeTodoCustomer({
        todo_id: todo.id,
        customer_id: selectedCustomerId,
      });

      if (!result.success) {
        toast({
          title: "Unable to change customer",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      const name =
        result.data.entity.Customer?.long_name ??
        customerOptions.find((option) => option.value === selectedCustomerId)?.label ??
        "";
      dispatch({
        type: "customerSaved",
        customer: { id: result.data.entity.customer_id, name },
      });
      toast({
        title: "Customer changed",
        description: name ? `The Todo is now assigned to ${name}.` : "The Todo's customer was changed.",
      });
    } catch {
      toast({
        title: "Unable to change customer",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  const handleSaveFlag = async () => {
    if (!todo || isFlagged || !reason.trim() || isSubmitting) return;

    dispatch({ type: "actionStarted", action: "save" });

    try {
      const result = await markTodoFlag({
        todo_id: todo.id,
        flagged_reason: reason,
      });

      if (!result.success) {
        toast({
          title: "Unable to flag Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      applySavedFlag(result.data.entity);
      toast({
        title: "Todo flagged",
        description: "The Todo was marked as flagged.",
      });
    } catch {
      toast({
        title: "Unable to flag Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  const handleSaveChanges = async () => {
    if (!todo || !canSaveChanges || isSubmitting) return;

    dispatch({ type: "actionStarted", action: "update" });

    try {
      const result = await updateTodoFlag({
        todo_id: todo.id,
        flagged_reason: reason,
      });

      if (!result.success) {
        toast({
          title: "Unable to update flag",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      applySavedFlag(result.data.entity);
      toast({
        title: "Flag updated",
        description: "The flag reason was updated.",
      });
    } catch {
      toast({
        title: "Unable to update flag",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  const handleRemoveFlag = async () => {
    if (!todo || !isFlagged || isSubmitting) return;

    dispatch({ type: "actionStarted", action: "remove" });

    try {
      const result = await removeTodoFlag({ todo_id: todo.id });

      if (!result.success) {
        toast({
          title: "Unable to remove flag",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      dispatch({ type: "flagRemoved" });
      toast({
        title: "Flag removed",
        description: "The Todo is no longer flagged.",
      });
    } catch {
      toast({
        title: "Unable to remove flag",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) requestClose();
        }}
      >
        {/* Height mirrors the width's margin (2.5vw a side, 5vw from `sm` up) so
            the gap around the modal is the same on every edge. */}
        <DialogContent className="flex h-[calc(100vh-5vw)] w-[95vw] max-w-[95vw] flex-col sm:h-[calc(100vh-10vw)] sm:max-w-[90vw]">
          <DialogHeader>
            <DialogTitle>Manage Todo</DialogTitle>
            <TodoContextLine todo={currentTodo} />
            <DialogDescription>
              Flag this automated Todo for review and view the call transcript
              it was created from.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 -mx-1 overflow-y-auto px-1">
            {(canChangeCustomer || showReviewActions) && (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {canChangeCustomer && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
                    <label htmlFor="manageTodoCustomer" className="text-sm font-medium">
                      Customer
                    </label>
                    <Combobox
                      id="manageTodoCustomer"
                      dwimOnChange
                      virtualizeList
                      isModal
                      options={customerOptions}
                      // A non-risk customer (e.g. the unmatched fallback) isn't an option; show the placeholder.
                      value={customerOptions.some((option) => option.value === selectedCustomerId) ? selectedCustomerId : ""}
                      // Re-selecting the current option clears it; keep the selection instead.
                      onValueChange={(value: string) =>
                        value && dispatch({ type: "customerSelected", customerId: value })
                      }
                      placeholder={customersResult.isLoading ? "Loading customers..." : "Choose a customer"}
                      disabled={isSubmitting}
                      className="w-[320px]"
                      popoverClassName="min-w-[320px]"
                    />
                    {hasUnsavedCustomer && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          dispatch({ type: "customerSelected", customerId: customer.id })
                        }
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      type="button"
                      data-testid="manage-todo-change-customer"
                      onClick={handleChangeCustomer}
                      disabled={!hasUnsavedCustomer || isSubmitting}
                    >
                      {pendingAction === "customer" ? "Saving..." : "Save Customer"}
                    </Button>
                  </div>
                )}
                {showReviewActions && (canHide || canApprove) && (
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                    {hasUnsavedChanges && (
                      <span className="text-xs text-muted-foreground">
                        {hasUnsavedReason
                          ? "Save or discard the flag reason first."
                          : "Save or cancel the customer change first."}
                      </span>
                    )}
                    {canHide && (
                      <Button
                        type="button"
                        variant="destructive"
                        data-testid="manage-todo-hide"
                        onClick={() =>
                          dispatch({ type: "hideDialogToggled", isOpen: true })
                        }
                        disabled={isSubmitting || hasUnsavedChanges}
                      >
                        Hide
                      </Button>
                    )}
                    {canApprove && (
                      <Button
                        type="button"
                        data-testid="manage-todo-approve"
                        onClick={handleApprove}
                        disabled={isSubmitting || hasUnsavedChanges}
                      >
                        {pendingAction === "approve" ? "Approving..." : "Approve"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Half the body height while open, whatever the transcript section is doing. */}
            <Collapsible
              open={isFlagOpen}
              onOpenChange={(isOpen) =>
                dispatch({ type: "flagSectionToggled", isOpen })
              }
              className="flex shrink-0 flex-col rounded-md border data-[state=open]:min-h-[16rem] data-[state=open]:basis-1/2"
            >
              <div className="flex flex-wrap items-center gap-2 pr-3 hover:bg-muted/40">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    data-testid="manage-todo-flag-toggle"
                    className="flex items-center gap-2 p-3 text-left text-sm font-medium"
                  >
                    {isFlagOpen ? (
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    )}
                    Flag Reason
                  </button>
                </CollapsibleTrigger>
                {isFlagged && (
                  <span className="flex items-center gap-1 text-xs font-semibold text-red-700">
                    <Flag className="h-3.5 w-3.5" aria-hidden="true" />
                    Flagged
                    <time dateTime={flag.date} className="font-normal text-red-700/80">
                      {moment
                        .tz(flag.date, CommonDestinationTimezone)
                        .format("MMM Do YYYY, h:mm A z")}
                    </time>
                  </span>
                )}
              </div>

              <CollapsibleContent className="min-h-0 flex-1 flex-col gap-4 border-t p-3 data-[state=open]:flex">
                <label htmlFor="todo-flag-reason" className="sr-only">
                  Flag Reason
                </label>
                <Textarea
                  id="todo-flag-reason"
                  value={reason}
                  onChange={(event) =>
                    dispatch({ type: "reasonChanged", reason: event.target.value })
                  }
                  maxLength={MAX_FLAG_REASON_CHARACTERS}
                  className="min-h-0 flex-1 resize-none"
                  placeholder="Describe why this Todo should be flagged..."
                  disabled={isSubmitting || isLocked}
                />

                <div className="flex items-start justify-between gap-4">
                  {isLocked ? (
                    <p className="text-xs text-muted-foreground">
                      Approved or hidden Todos cannot be flagged or edited.
                    </p>
                  ) : (
                    <CharacterCounter count={reason.length} max={MAX_FLAG_REASON_CHARACTERS} />
                  )}
                  {isFlagged ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleRemoveFlag}
                        disabled={isSubmitting || isLocked}
                      >
                        {pendingAction === "remove" ? "Removing..." : "Remove Flag"}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSaveChanges}
                        disabled={!canSaveChanges || isSubmitting || isLocked}
                      >
                        {pendingAction === "update" ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleSaveFlag}
                      disabled={!reason.trim() || isSubmitting || isLocked}
                    >
                      {pendingAction === "save" ? "Saving..." : "Save Flag"}
                    </Button>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible
              open={isTranscriptOpen}
              onOpenChange={(isOpen) =>
                dispatch({ type: "transcriptToggled", isOpen })
              }
              className="flex flex-col rounded-md border data-[state=open]:min-h-[20rem] data-[state=open]:flex-1"
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium hover:bg-muted/40"
                >
                  {isTranscriptOpen ? (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  )}
                  Call Transcript
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="min-h-0 flex-1 flex-col border-t p-3 data-[state=open]:flex">
                <TodoTranscriptContent {...transcriptState} />
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HideTodoDialog
        todo={isHideDialogOpen ? currentTodo : null}
        onClose={() =>
          dispatch({ type: "hideDialogToggled", isOpen: false })
        }
        onHidden={closeWithoutConfirmation}
      />

      <Dialog
        open={isDiscardDialogOpen}
        onOpenChange={(isOpen) =>
          dispatch({ type: "discardPromptToggled", isOpen })
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Discard unsaved changes?
            </DialogTitle>
            <DialogDescription>
              {hasUnsavedReason && hasUnsavedCustomer
                ? "Your flag reason and customer change have not been saved."
                : hasUnsavedCustomer
                  ? "Your customer change has not been saved."
                  : "Your flag reason has not been saved."}{" "}
              Discarding cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                dispatch({ type: "discardPromptToggled", isOpen: false })
              }
            >
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={closeWithoutConfirmation}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
